import {inject, Injector, NgModule} from '@angular/core';
import {Router, RouterModule, Routes} from '@angular/router';
import {environment} from '../environments/environment';
import {AuthenticatedGuard} from './shared/guards/authenticated/authenticated-guard.service';
import {QmsGuard} from './px/qms/qms.guard';
import {CustomersGuard} from './px/customers/customers.guard';
import {EnrolmentGuard} from './px/enrolment/enrolment-guard.service';
import {PasswordResetGuard} from './prudent-plus/password-reset/password-reset.guard';
import {IdentityProviderGuard} from './prudent-plus/login-identity-provider/login-identity-provider.guard';
import {ClaimsGuard} from './px/claims/claims.guard';
import {InsurerService} from './shared/services/insurer/insurer.service';

const routes: Routes = [
  {
    path: '',
    redirectTo: environment.appConfig.homeRouterLink,
    pathMatch: 'full',
    data: {
      modules: ['all'],
    },
  },
  {
    path: 'airtel-redirect',
    data: {
      modules: ['prudent-plus'],
    },
    loadComponent: () => import('./prudent-plus/airtel-redirect/airtel-redirect.component').then((m) => m.AirtelRedirectComponent),
  },
  {
    path: 'dashboard',
    data: {
      modules: [
        'customers',
        'access-control',
        'configure-proposal',
        'compass',
        'enrolment',
      ],
    },
    loadComponent: () => import('./px/app-selector/app-selector.component').then((m) => m.AppSelectorComponent),
  },
  {
    path: 'compass',
    data: {
      modules: ['compass'],
    },
    loadChildren: () => import('./px/qms/proposal/proposal.module').then((m) => m.ProposalModule),
    canActivate: [
      AuthenticatedGuard,
      QmsGuard,
    ],
  },
  {
    path: 'customers',
    data: {
      modules: ['customers'],
    },
    loadChildren: () => import('./px/customers/customers.module').then((m) => m.CustomersModule),
    canActivate: [
      AuthenticatedGuard,
      CustomersGuard,
    ],
  },
  {
    path: 'configure-proposal',
    data: {
      modules: ['compass'],
    },
    loadChildren: () => import('./px/qms/configuration/configuration.module').then((m) => m.ConfigurationModule),
    canActivate: [
      AuthenticatedGuard,
      QmsGuard,
    ],
  },
  {
    path: 'billing',
    data: {
      modules: ['billing'],
    },
    loadChildren: () => import('./px/billing/billing.module').then((m) => m.BillingModule),
    canActivate: [
      AuthenticatedGuard,
      () => inject(InsurerService).loadInsurers(true),
    ],
  },
  {
    path: 'claims',
    data: {
      modules: ['claims'],
    },
    loadChildren: () => import('./px/claims/claims.module').then((m) => m.ClaimsModule),
    canActivate: [
      AuthenticatedGuard,
      ClaimsGuard,
    ],
  },
  {
    path: 'settings',
    data: {
      modules: ['access-control'],
    },
    loadChildren: () => import('./px/global-settings/global-settings.module').then((m) => m.GlobalSettingsModule),
    canActivate: [AuthenticatedGuard],
  },
  {
    path: 'enrolment',
    data: {
      modules: ['prudent-plus-backend'],
    },
    loadChildren: () => import('./px/enrolment/enrolment.module').then((m) => m.EnrolmentModule),
    canActivate: [
      AuthenticatedGuard,
      EnrolmentGuard,
    ],
  },
  {
    path: 'dashboard',
    data: {
      modules: ['prudent-plus'],
    },
    loadChildren: () => import('./prudent-plus/prudent-plus.module').then((m) => m.PrudentPlusModule),
  },
  {
    path: 'dashboard',
    data: {
      modules: ['prudent-360'],
    },
    loadChildren: () => import('./prudent-360/prudent360.module').then((m) => m.Prudent360Module),
    canActivate: [
      AuthenticatedGuard,
      QmsGuard,
    ],
  },
  {
    path: 'dashboard',
    data: {
      modules: ['readme'],
    },
    loadChildren: () => import('./readme/readme.module').then((m) => m.ReadmeModule),
    canActivate: [AuthenticatedGuard],
  },
  {
    path: 'login',
    data: {
      modules: ['prudent-360'],

    },
    loadComponent: () => import('./prudent-360/p360-login/p360-login.component').then((m) => m.P360LoginComponent),
  },
  {
    path: 'login',
    data: {
      modules: [
        'customers',
        'access-control',
        'configure-proposal',
        'compass',
        'enrolment',
      ],
    },
    loadComponent: () => import('./px/shared/px-login/px-login.component').then((m) => m.PxLoginComponent),
  },
  {
    path: 'login',
    data: {
      modules: ['prudent-plus'],
    },
    loadComponent: () => import('./prudent-plus/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'reset-password',
    data: {
      modules: ['prudent-plus'],
    },
    loadComponent: () => import('./prudent-plus/password-reset/password-reset.component').then((m) => m.PasswordResetComponent),
    canActivate: [PasswordResetGuard],
  },
  {
    path: 'identity-provider',
    data: {
      modules: ['prudent-plus'],
    },
    loadComponent: () => import('./prudent-plus/login-identity-provider/login-identity-provider.component').then((m) => m.LoginIdentityProviderComponent),
    canActivate: [IdentityProviderGuard],
  },
  {
    path: 'mfa',
    data: {
      modules: ['prudent-plus'],
    },
    loadComponent: () => import('./prudent-plus/login-mfa/login-mfa.component').then((m) => m.LoginMfaComponent),
  },
  {
    path: 'unauthorized',
    data: {
      modules: ['all'],
    },
    loadComponent: () => import('./shared/views/restricted/restricted.component').then((m) => m.RestrictedComponent),
  },
  {
    path: 'codebook',
    data: {
      modules: ['codebook'],
    },
    loadChildren: () => import('./codebook/codebook.module').then((m) => m.CodebookModule),
  },
  {
    path: '**',
    data: {
      modules: ['all'],
    },
    loadComponent: () => import('./shared/views/page-not-found/page-not-found.component').then((m) => m.PageNotFoundComponent),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {
}

export function loadRoutes(injector: Injector) {
  return () => {
    const moduleId = new Set<string>(environment.appConfig.moduleIds);
    const filteredRoutes = routes.filter((r) => {
      const modules = r.data?.['modules'] || []; // Default to an empty array if 'modules' is undefined

      return modules.find((module: string) => module === 'all' || moduleId.has(module));
    });
    const router: Router = injector.get(Router);

    router.resetConfig(filteredRoutes);
  };
}
